/**
 * F7 — Presupuesto anual por partidas.
 *
 * Lo primero que se ve son las ALERTAS, no la tabla: un presupuesto sirve para
 * enterarte de que algo se está yendo, no para admirar la planificación. Lo
 * ejecutado sale del diario en cada consulta, con los mismos filtros que la
 * Cuenta de Resultados, así que no puede contradecirla.
 */
import { useCallback, useEffect, useState } from 'react'
import { AlertCircle, AlertTriangle, Check, Copy, Loader2, Plus, Target, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../lib/api'
import { confirmDialog, toast } from '../lib/dialogs'
import { formatCents, parseEurosToCents } from '../lib/money'
import { useTableSort } from '../lib/useTableSort'
import Modal from '../components/ui/Modal'
import { useAuth } from '../contexts/AuthContext'

interface BudgetLineResult {
  id: string
  accountCode: string
  label: string
  budgetedCents: number
  actualCents: number
  remainingCents: number
  consumedPct: number | null
  alertThresholdPct: number
  status: 'OK' | 'AVISO' | 'EXCEDIDO'
  nature: 'GASTO' | 'INGRESO' | 'OTRA'
  notes: string | null
}

interface BudgetResult {
  year: number
  lines: BudgetLineResult[]
  totals: {
    expenseBudgetedCents: number
    expenseActualCents: number
    incomeBudgetedCents: number
    incomeActualCents: number
  }
  alerts: BudgetLineResult[]
}

const inputCls = 'rounded-md border px-2 py-1.5 text-sm'
const inputStyle = { background: 'var(--bg-input)', borderColor: 'var(--border)' } as const

function errorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    return (err.body as { error?: string } | undefined)?.error ?? err.message
  }
  return err instanceof Error ? err.message : fallback
}

export default function AccountingBudget() {
  const { t } = useTranslation(['budget', 'common'])
  const auth = useAuth()
  const canWrite = auth.status === 'authenticated' && auth.user.role === 'ADMIN'
  const currentYear = new Date().getFullYear()

  const [year, setYear] = useState(currentYear)
  const [data, setData] = useState<BudgetResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await api.get<BudgetResult>(`/accounting/budget?year=${year}`))
    } catch (err) {
      setError(errorText(err, t('errors.load')))
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [year, t])

  useEffect(() => {
    void load()
  }, [load])

  // Orden por columna de las partidas (sin orden elegido, el de siempre). «Consumido» ordena por el porcentaje; sin él («—»), al final.
  const { rows: lineRows, header } = useTableSort(data?.lines, {
    // Cuenta o PREFIJO de 1 a 8 dígitos: se completa con ceros a 8 para ordenar como siempre (por texto), no como número.
    account: (l) => l.accountCode.padEnd(8, '0'),
    line: (l) => l.label,
    budgeted: (l) => l.budgetedCents,
    actual: (l) => l.actualCents,
    remaining: (l) => l.remainingCents,
    consumed: (l) => l.consumedPct,
  })

  const remove = async (line: BudgetLineResult) => {
    if (!(await confirmDialog({ message: t('confirmDelete', { name: line.label }), tone: 'danger' })))
      return
    try {
      await api.delete(`/accounting/budget/${line.id}`)
      await load()
    } catch (err) {
      setError(errorText(err, t('errors.generic')))
    }
  }

  const copyPrevious = async () => {
    if (!(await confirmDialog(t('confirmCopy', { from: year - 1, to: year })))) return
    try {
      const r = await api.post<{ copied: number; skipped: number }>('/accounting/budget/copy', {
        fromYear: year - 1,
        toYear: year,
      })
      toast.success(t('copyDone', r))
      await load()
    } catch (err) {
      setError(errorText(err, t('errors.generic')))
    }
  }

  const pctBar = (l: BudgetLineResult) => {
    const pct = Math.max(0, Math.min(100, l.consumedPct ?? 0))
    const tone =
      l.status === 'EXCEDIDO'
        ? 'bg-rose-500'
        : l.status === 'AVISO'
          ? 'bg-amber-500'
          : 'bg-emerald-500'
    return (
      <div className="h-1.5 rounded-full overflow-hidden w-24" style={{ background: 'var(--border)' }}>
        <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    )
  }

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <Target size={20} className="text-emerald-600 dark:text-emerald-300" />
            {t('title')}
          </h1>
          <p className="text-xs t-muted">{t('subtitle')}</p>
        </div>
        <div className="flex items-end gap-2">
          <label className="text-xs t-muted flex flex-col gap-1">
            {t('year')}
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className={inputCls}
              style={inputStyle}
            >
              {Array.from({ length: 6 }, (_, i) => currentYear + 1 - i).map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
          {canWrite && (
            <>
              <button
                type="button"
                onClick={() => void copyPrevious()}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-base text-sm hover:bg-hover t-primary"
              >
                <Copy size={15} /> {t('copyPrevious', { year: year - 1 })}
              </button>
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-jr2-600 hover:bg-jr2-500 text-white text-sm"
              >
                <Plus size={15} /> {t('newLine')}
              </button>
            </>
          )}
        </div>
      </header>

      {error && (
        <div className="surface p-3 flex items-start gap-2 border-rose-500/40">
          <AlertCircle size={16} className="text-rose-400 mt-0.5 shrink-0" />
          <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p>
        </div>
      )}

      {/* Las alertas van ARRIBA: es para lo que sirve un presupuesto. */}
      {data && data.alerts.length > 0 && (
        <section className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
          <h2 className="text-sm font-medium text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
            <AlertTriangle size={15} /> {t('alerts.title', { count: data.alerts.length })}
          </h2>
          <ul className="mt-1.5 space-y-0.5 text-xs text-amber-700 dark:text-amber-300">
            {data.alerts.map((a) => (
              <li key={a.id}>
                <span className="font-mono mr-2">{a.accountCode}</span>
                {a.label} —{' '}
                {a.nature === 'INGRESO'
                  ? t('alerts.incomeShort', { pct: a.consumedPct ?? 0 })
                  : a.status === 'EXCEDIDO'
                    ? t('alerts.exceeded', {
                        pct: a.consumedPct ?? 0,
                        over: formatCents(-a.remainingCents),
                      })
                    : t('alerts.near', { pct: a.consumedPct ?? 0 })}
              </li>
            ))}
          </ul>
        </section>
      )}
      {data && data.alerts.length === 0 && data.lines.length > 0 && (
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-300 flex items-center gap-2">
          <Check size={15} /> {t('alerts.none')}
        </div>
      )}

      {data && data.lines.length > 0 && (
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card label={t('totals.expenseBudget')} value={formatCents(data.totals.expenseBudgetedCents)} />
          <Card label={t('totals.expenseActual')} value={formatCents(data.totals.expenseActualCents)} accent />
          <Card label={t('totals.incomeBudget')} value={formatCents(data.totals.incomeBudgetedCents)} />
          <Card label={t('totals.incomeActual')} value={formatCents(data.totals.incomeActualCents)} accent />
        </section>
      )}

      <section className="surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs t-muted uppercase tracking-wider" style={{ borderBottom: '1px solid var(--border)' }}>
              {header('account', t('columns.account'))}
              {header('line', t('columns.line'))}
              {header('budgeted', t('columns.budgeted'), { align: 'right' })}
              {header('actual', t('columns.actual'), { align: 'right' })}
              {header('remaining', t('columns.remaining'), { align: 'right' })}
              {header('consumed', t('columns.consumed'))}
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-xs t-muted">
                  <Loader2 size={14} className="animate-spin inline mr-1.5" /> {t('loading')}
                </td>
              </tr>
            ) : (data?.lines.length ?? 0) === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-xs t-muted">{t('empty')}</td>
              </tr>
            ) : (
              lineRows.map((l) => (
                <tr key={l.id} style={{ borderBottom: '1px solid var(--border)' }} className="t-primary">
                  <td className="px-3 py-1.5 font-mono text-xs whitespace-nowrap">{l.accountCode}</td>
                  <td className="px-3 py-1.5 max-w-xs truncate" title={l.notes ?? l.label}>
                    {l.label}
                    <span className="ml-2 text-[10px] t-faint">{t(`nature.${l.nature}`)}</span>
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono text-xs">{formatCents(l.budgetedCents)}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-xs">{formatCents(l.actualCents)}</td>
                  <td
                    className={`px-3 py-1.5 text-right font-mono text-xs ${
                      l.remainingCents < 0 ? 'text-rose-700 dark:text-rose-300' : ''
                    }`}
                  >
                    {formatCents(l.remainingCents)}
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-2">
                      {pctBar(l)}
                      <span className="text-xs tabular-nums t-muted w-12 text-right">
                        {l.consumedPct === null ? '—' : `${l.consumedPct}%`}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    {canWrite && (
                      <button
                        type="button"
                        onClick={() => void remove(l)}
                        title={t('common:actions.delete')}
                        className="p-1 rounded hover:bg-hover text-rose-600 dark:text-rose-400"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>

      {creating && (
        <NewLineModal
          year={year}
          onClose={() => setCreating(false)}
          onCreated={async () => {
            setCreating(false)
            await load()
          }}
        />
      )}
    </div>
  )
}

function Card({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className={`surface p-3 ${accent ? 'ring-1 ring-jr2-500/30' : ''}`}>
      <div className="text-[10px] uppercase tracking-wider t-muted">{label}</div>
      <div className="text-lg font-semibold t-primary tabular-nums">{value}</div>
    </div>
  )
}

function NewLineModal({
  year,
  onClose,
  onCreated,
}: {
  year: number
  onClose: () => void
  onCreated: () => Promise<void> | void
}) {
  const { t } = useTranslation(['budget', 'common'])
  const [accountCode, setAccountCode] = useState('')
  const [label, setLabel] = useState('')
  const [amount, setAmount] = useState('')
  const [threshold, setThreshold] = useState('80')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    setSaving(true)
    setError(null)
    try {
      await api.post('/accounting/budget', {
        year,
        accountCode: accountCode.trim(),
        label: label.trim() || null,
        amountCents: parseEurosToCents(amount),
        alertThresholdPct: Number(threshold) || 80,
      })
      await onCreated()
    } catch (err) {
      setError(errorText(err, t('errors.generic')))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open onClose={onClose} title={t('newLine')} maxWidth="max-w-md">
      <div className="space-y-3">
        <label className="block">
          <span className="text-xs t-muted block mb-1">{t('fields.account')}</span>
          <input
            value={accountCode}
            onChange={(e) => setAccountCode(e.target.value)}
            placeholder="629"
            className={`${inputCls} w-full font-mono`}
            style={inputStyle}
          />
          <span className="text-[10px] t-faint block mt-0.5">{t('fields.accountHint')}</span>
        </label>
        <label className="block">
          <span className="text-xs t-muted block mb-1">{t('fields.label')}</span>
          <input value={label} onChange={(e) => setLabel(e.target.value)} className={`${inputCls} w-full`} style={inputStyle} />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs t-muted block mb-1">{t('fields.amount')}</span>
            <input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="12.000,00" className={`${inputCls} w-full text-right font-mono`} style={inputStyle} />
          </label>
          <label className="block">
            <span className="text-xs t-muted block mb-1">{t('fields.threshold')}</span>
            <input type="number" min={1} max={200} value={threshold} onChange={(e) => setThreshold(e.target.value)} className={`${inputCls} w-full text-right font-mono`} style={inputStyle} />
          </label>
        </div>

        {error && (
          <p className="text-xs text-rose-700 dark:text-rose-300 flex items-center gap-1.5">
            <AlertCircle size={12} /> {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-md text-sm t-muted hover:bg-hover">
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={saving || !accountCode.trim() || !amount.trim()}
            className="px-3 py-1.5 rounded-md bg-jr2-600 hover:bg-jr2-500 text-white text-sm disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {saving && <Loader2 size={13} className="animate-spin" />}
            {t('common:actions.save')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
